import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { COLORS, FONTS } from "@/lib/constants";
import { INK, MUTED_36, MUTED_60, FONT as YFONT, ROOMS } from "@/lib/yuoDesign";
import StoneOrb from "@/components/yuo/StoneOrb";
import { useResponsive } from "@/lib/responsive";
import { api } from "@/lib/api";

/**
 * "Three Scriptures" — one dark chamber, three living traditions.
 *
 * Fully rebuilt on the frosted-glass / stone-orb material to match the rest
 * of the app: no borders, no dark rectangles, no arrow-shaped back button,
 * no legacy 1px dividers.
 */
export default function LibraryDaily() {
  const navigate = useNavigate();
  const { readingMaxWidth, gutter, isMobile } = useResponsive();

  const [lang, setLang] = useState("en");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .dailyTriad(lang)
      .then((d) => !cancelled && setData(d))
      .catch((e) => !cancelled && setErr(e?.message || "Could not load the triad"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [lang]);

  const dateLabel = useMemo(() => {
    if (!data?.date) return "";
    try {
      const d = new Date(data.date + "T00:00:00Z");
      return d.toLocaleDateString("en-US", {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
    } catch { return data.date; }
  }, [data?.date, lang]);

  const openInReader = (item) => {
    navigate(
      `/library/read?source=${item.source}&code=${item.code}&chapter=${item.chapter}&lang=${lang}#verse-${item.verse}`,
    );
  };

  return (
    <div
      data-testid="library-daily-page"
      style={{
        position: "relative",
        minHeight: "100vh",
        background: "var(--yuo-bg)",
        color: INK,
        fontFamily: YFONT,
        overflow: "clip",
      }}
    >
      {/* Ambient wash — three radial pools that dissolve into the chamber */}
      <div aria-hidden style={{
        position: "absolute", inset: "2%", pointerEvents: "none",
        background:
          "radial-gradient(38% 26% at 22% 12%, rgba(var(--role-yellow-rgb),0.08), transparent 78%)," +
          "radial-gradient(34% 24% at 82% 34%, rgba(123,104,238,0.07), transparent 76%)," +
          "radial-gradient(40% 30% at 50% 92%, rgba(47,180,138,0.06), transparent 80%)",
        animation: "yuo-drift 38s ease-in-out infinite",
      }} />

      <div style={{
        position: "relative",
        maxWidth: readingMaxWidth || 1180,
        margin: "0 auto",
        padding: `${(gutter || 20) + 12}px ${gutter || 20}px 120px`,
      }}>
        {/* Header — stone back + language pills */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <button
            onClick={() => navigate("/library")}
            data-testid="daily-back"
            style={{
              display: "inline-flex", alignItems: "center", gap: 12,
              background: "transparent", border: "none", padding: 0, cursor: "pointer",
              color: "var(--fg-55)", fontFamily: YFONT, fontSize: 11,
              letterSpacing: "0.24em", textTransform: "uppercase",
            }}
          >
            <StoneOrb color="library" size={14} />
            <span>library</span>
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            {["ru", "en", "pt"].map((l) => {
              const active = lang === l;
              return (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  data-testid={`daily-lang-${l}`}
                  style={{
                    padding: "6px 16px", borderRadius: 999, border: "none", cursor: "pointer",
                    background: active
                      ? "radial-gradient(120% 160% at 30% 0%, rgba(var(--role-yellow-rgb),0.34), rgba(var(--role-yellow-rgb),0.08))"
                      : "var(--fg-05)",
                    boxShadow: active ? "0 0 22px rgba(var(--role-yellow-rgb),0.32)" : "none",
                    color: active ? "var(--lit-yellow-20)" : "var(--fg-55)",
                    fontFamily: YFONT, fontSize: 10.5, letterSpacing: "0.22em", textTransform: "uppercase",
                    transition: ".6s",
                  }}
                >
                  {l}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginTop: 42, maxWidth: 720,
          animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both" }}>
          <div style={{ color: "rgba(var(--role-yellow-rgb),0.7)", fontSize: 10.5,
            letterSpacing: "0.28em", textTransform: "uppercase", marginBottom: 12 }}>
            Three Scriptures · Word of the day
          </div>
          <h1 style={{ margin: 0, fontFamily: YFONT, fontSize: isMobile ? 32 : 40,
            fontWeight: 300, letterSpacing: "0.02em", color: INK }}>
            One window · three traditions
          </h1>
          <p style={{ margin: "16px 0 0", fontSize: 13, lineHeight: 1.8,
            color: "var(--fg-42)" }}>
            {dateLabel && <span style={{ textTransform: "capitalize" }}>{dateLabel}</span>}
            {" · "}
            Refreshes every morning.
          </p>
        </div>

        {loading && (
          <div data-testid="daily-loading" style={{
            marginTop: 60, padding: 40, textAlign: "center",
            color: MUTED_36, fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
          }}>
            assembling today's triad…
          </div>
        )}
        {err && !loading && (
          <div data-testid="daily-error" style={{
            marginTop: 40, padding: "16px 22px", borderRadius: 999,
            background: "rgba(239,68,68,0.10)", color: "var(--lit-coral-9)",
            fontSize: 12, letterSpacing: "0.14em",
          }}>
            {err}
          </div>
        )}

        {!loading && data?.items && (
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: 26,
            marginTop: 40,
          }}>
            {data.items.map((it) => (
              <TriadCard
                key={it.tradition}
                item={it}
                lang={lang}
                onOpen={() => openInReader(it)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TriadCard({ item, lang, onOpen }) {
  const isTorah = item.tradition === "torah";
  const isQuran = item.tradition === "quran";
  const label =
    item.tradition === "bible" ? "Bible"
    : isTorah ? "Torah · תּוֹרָה"
    : isQuran ? "Qur'an · ٱلْقُرْآن"
    : item.tradition;
  const accentRgb =
    item.tradition === "bible" ? "123,104,238"
    : isTorah ? "233,196,106"
    : "47,180,138";
  const accentHex =
    item.tradition === "bible" ? "var(--lit-purple-17)"
    : isTorah ? "#E9C46A"
    : "#2FB48A";
  const original = isTorah ? item.text_he : isQuran ? item.text_ar : null;
  const originalDir = original ? "rtl" : "ltr";

  return (
    <div
      data-testid={`triad-card-${item.tradition}`}
      style={{
        position: "relative",
        padding: "26px 26px 22px",
        borderRadius: 32,
        background: `radial-gradient(120% 100% at 26% 0%, rgba(${accentRgb},0.18), rgba(${accentRgb},0.04) 58%, var(--dk-101220-35) 100%)`,
        boxShadow: `0 0 60px rgba(${accentRgb},0.18) inset`,
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        display: "flex", flexDirection: "column", gap: 16, minHeight: 340,
        animation: "yuo-emerge 1s cubic-bezier(.2,.7,.2,1) both",
      }}
    >
      <div style={{
        display: "inline-flex", alignItems: "center", gap: 10, alignSelf: "flex-start",
        padding: "6px 14px", borderRadius: 999,
        background: `rgba(${accentRgb},0.14)`,
      }}>
        <span style={{
          width: 10, height: 10, borderRadius: "50%",
          background: `radial-gradient(34% 30% at 32% 26%, #fff, ${accentHex} 22%, #05060c 100%)`,
          boxShadow: `0 0 12px ${accentHex}`,
        }} />
        <span style={{ color: accentHex, fontFamily: YFONT, fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", fontWeight: 500 }}>
          {label}
        </span>
      </div>

      <div style={{ color: INK, fontFamily: YFONT, fontSize: 13.5, letterSpacing: 0.1 }}>
        {item.ref_human}
      </div>

      {/* Primary text */}
      <div style={{
        color: INK, fontFamily: YFONT,
        fontSize: 17, lineHeight: 1.7, flex: 1,
        letterSpacing: 0.02,
      }}>
        &ldquo;{item.text}&rdquo;
      </div>

      {/* Original script — Hebrew or Arabic */}
      {original && (
        <div dir={originalDir} style={{
          fontSize: 21, lineHeight: 1.7,
          color: `rgba(${accentRgb},0.85)`,
          padding: "14px 16px", borderRadius: 20,
          background: `rgba(${accentRgb},0.06)`,
          boxShadow: `0 0 22px rgba(${accentRgb},0.10) inset`,
          textAlign: "right",
          direction: originalDir,
        }}>
          {original}
        </div>
      )}

      <button
        onClick={onOpen}
        data-testid={`triad-open-${item.tradition}`}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 12,
          padding: "12px 22px", borderRadius: 999, border: "none", cursor: "pointer",
          background: `radial-gradient(120% 160% at 20% 0%, rgba(${accentRgb},0.30), rgba(${accentRgb},0.06))`,
          boxShadow: `0 0 22px rgba(${accentRgb},0.28)`,
          color: INK, fontFamily: YFONT, fontSize: 10.5,
          letterSpacing: "0.22em", textTransform: "uppercase",
          transition: ".6s",
        }}
      >
        <span style={{
          width: 12, height: 12, borderRadius: "50%",
          background: `radial-gradient(34% 30% at 32% 26%, #fff, ${accentHex} 22%, #05060c 100%)`,
          boxShadow: `0 0 12px ${accentHex}`,
        }} />
        Open chapter
      </button>
    </div>
  );
}
